<template>
  <div class="fees-list">
    <Table
      :contents="state.fees"
      :hidePagination="true"
      :hideTableTop="true"
      :loading="state.loading"
      :noClickEvent="true"
      :displayId="false"
      darkenAll
    >
      <template v-slot:empty="{ item }">
        <div class="fees-list__actions" @click.stop>
          <button
            type="button"
            class="fees-list__actions-btn"
            aria-label="Row actions"
            @click="toggleActions(item)"
          >
            <svg
              width="4"
              height="16"
              viewBox="0 0 4 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <circle cx="2" cy="2" r="2" fill="#919EAB" fill-opacity="0.6" />
              <circle cx="2" cy="8" r="2" fill="#919EAB" fill-opacity="0.6" />
              <circle cx="2" cy="14" r="2" fill="#919EAB" fill-opacity="0.6" />
            </svg>
          </button>
          <div v-if="isActionsOpen(item)" class="fees-list__actions-menu">
            <button type="button" @click="handleEdit(item)">Edit</button>
            <button
              type="button"
              class="fees-list__delete"
              @click="handleDelete(item)"
            >
              Delete
            </button>
          </div>
        </div>
      </template>
      <template v-slot:nocontent>
        <div class="fees-list__empty">
          <h1>No Bank Fees</h1>
          <p>All bank fees will show here</p>
        </div>
      </template>
    </Table>

    <div
      v-if="state.deleteFee"
      class="fees-list__overlay"
      @click.self="closeDeleteConfirm"
    >
      <div class="fees-list__dialog" role="dialog" aria-modal="true">
        <h2 class="fees-list__dialog-title">Delete Bank Fee</h2>
        <p class="fees-list__dialog-text">
          Remove the fee for
          <strong>{{ state.deleteFee["BANK NAME"] }}</strong>
          ({{ state.deleteFee["TRANSACTION TYPE"] }})? This cannot be undone.
        </p>
        <div class="fees-list__dialog-actions">
          <button
            type="button"
            class="fees-list__btn-cancel"
            @click="closeDeleteConfirm"
          >
            Cancel
          </button>
          <button
            type="button"
            class="fees-list__btn-delete"
            :disabled="state.deleting"
            @click="confirmDelete"
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import Vue from "vue";
import Table from "@/components/table.vue";
import {
  reactive,
  onBeforeMount,
  onMounted,
  onBeforeUnmount,
} from "@vue/composition-api";

const getActionKey = (item) =>
  `${item.id}-${item["BANK NAME"]}-${item["TRANSACTION TYPE"]}`;

const getFeeId = (fee) => fee.id || fee._id || fee.bank_fee_id;

const resolveBankName = (banks, bankCode) => {
  if (!bankCode) return "";
  const bank = banks.find(
    ({ code, cbn_code }) => code == bankCode || cbn_code === bankCode
  );
  return bank ? bank.name || bank.bank : bankCode;
};

const formatFeeRow = (fee, getBankName) => ({
  id: getFeeId(fee),
  "BANK NAME": getBankName(fee.bank_code) || "",
  "TRANSACTION TYPE": fee.transaction_type || "",
  "FEE TYPE": fee.fee_type || "",
  VALUE: fee.fee_value != null ? fee.fee_value : "",
  ACTIONS: "",
});

const parseRawFees = (body) => {
  const data = body.data || body;
  const list = Array.isArray(data) ? data : [data];
  return list.filter(Boolean);
};

const notify = (type, text) => {
  Vue.notify({
    group: "notifications-default",
    type: `${type} text-white`,
    title: type === "success" ? "Success" : "Error",
    text,
  });
};

const deleteBankFee = (id) => Vue.http.delete(`admin/bank-fees/${id}`);

const getErrorMessage = (err, fallback) =>
  (err.body && err.body.message) || fallback;

export default {
  components: { Table },
  setup(props, { root }) {
    const state = reactive({
      fees: [],
      rawFees: [],
      loading: true,
      deleteFee: null,
      deleting: false,
      openActionsKey: null,
    });

    const closeActions = () => {
      state.openActionsKey = null;
    };

    const toggleActions = (item) => {
      const key = getActionKey(item);
      state.openActionsKey = state.openActionsKey === key ? null : key;
    };

    const isActionsOpen = (item) =>
      state.openActionsKey === getActionKey(item);

    const getBankName = (bankCode) =>
      resolveBankName(root.$store.getters.getBankList || [], bankCode);

    const loadFees = async () => {
      state.loading = true;
      try {
        const response = await Vue.http.get("admin/bank-fees");
        state.rawFees = parseRawFees(response.body);
        state.fees = state.rawFees.map((fee) => formatFeeRow(fee, getBankName));
      } catch (err) {
        notify("error", getErrorMessage(err, "Failed to load bank fees"));
      } finally {
        state.loading = false;
      }
    };

    const findRawFee = (item) =>
      state.rawFees.find((fee) => String(getFeeId(fee)) === String(item.id));

    const openEdit = (item) => {
      const rawFee = findRawFee(item);
      const feeId = rawFee ? getFeeId(rawFee) : item.id;
      if (!feeId) {
        return notify("error", "Unable to edit this bank fee");
      }
      sessionStorage.setItem("bankFeeEdit", JSON.stringify(rawFee || item));
      root.$router.push({
        name: "settings-fee-edit",
        params: { id: String(feeId) },
      });
    };

    const openDeleteConfirm = (item) => {
      state.deleteFee = item;
    };

    const closeDeleteConfirm = () => {
      state.deleteFee = null;
    };

    const handleEdit = (item) => {
      closeActions();
      openEdit(item);
    };

    const handleDelete = (item) => {
      closeActions();
      openDeleteConfirm(item);
    };

    const confirmDelete = async () => {
      state.deleting = true;
      try {
        await deleteBankFee(state.deleteFee.id);
        notify("success", "Bank fee deleted successfully");
        closeDeleteConfirm();
        await loadFees();
      } catch (err) {
        notify("error", getErrorMessage(err, "Failed to delete bank fee"));
      }
      state.deleting = false;
    };

    const onDocumentClick = () => closeActions();

    onBeforeMount(loadFees);
    onMounted(() => document.addEventListener("click", onDocumentClick));
    onBeforeUnmount(() =>
      document.removeEventListener("click", onDocumentClick)
    );

    return {
      state,
      toggleActions,
      isActionsOpen,
      handleEdit,
      handleDelete,
      openDeleteConfirm,
      closeDeleteConfirm,
      confirmDelete,
    };
  },
};
</script>
<style lang="scss">
.fees-list {
  display: flex;
  flex-direction: column;
  margin-bottom: 50px;

  &__empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    padding: 90px 26px;

    h1 {
      font-size: 18px;
      font-weight: normal;
      margin-bottom: 8px;
    }

    p {
      font-size: 14px;
      opacity: 0.7;
    }
  }

  ::v-deep .table-component {
    overflow: visible;

    .card-table {
      overflow: visible;
      table-layout: fixed;
      width: 100%;
    }

    th:nth-child(1),
    td:nth-child(1) {
      width: 0;
      padding: 0;
      border: none;
    }

    th:nth-child(2),
    td:nth-child(2) {
      width: 45%;
      white-space: normal;
      word-break: break-word;
    }

    th:nth-child(3),
    td:nth-child(3) {
      width: 160px;
    }

    th:nth-child(4),
    td:nth-child(4) {
      width: 110px;
    }

    th:nth-child(5),
    td:nth-child(5) {
      width: 72px;
      text-align: right;
    }

    tbody tr td:last-child {
      overflow: visible;
      position: relative;
      width: 56px;
      text-align: center;
      vertical-align: middle;
    }
  }

  &__actions {
    position: relative;
    display: inline-flex;
    justify-content: center;
  }

  &__actions-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 8px 12px;
    border: none;
    background: transparent;
    cursor: pointer;
    border-radius: 4px;

    &:hover {
      background: #ebf0f4;
    }
  }

  &__actions-menu {
    position: absolute;
    top: calc(100% + 4px);
    right: 0;
    z-index: 1200;
    min-width: 120px;
    background: #fff;
    border-radius: 4px;
    box-shadow: 0 4px 16px rgba(37, 40, 43, 0.16);
    overflow: hidden;

    button {
      display: block;
      width: 100%;
      padding: 10px 16px;
      border: none;
      background: #fff;
      text-align: left;
      font-size: 14px;
      color: #261940;
      cursor: pointer;

      &:hover {
        background: #f5f7fa;
      }
    }
  }

  &__delete {
    color: #e00b5b !important;
  }

  &__overlay {
    position: fixed;
    inset: 0;
    background: rgba(20, 20, 43, 0.4);
    backdrop-filter: blur(4px);
    z-index: 2000;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24px 16px;
  }

  &__dialog {
    background: #fff;
    border-radius: 12px;
    max-width: 440px;
    width: 100%;
    padding: 22px 26px 24px;
    box-shadow: 0 20px 50px rgba(15, 23, 42, 0.18);
  }

  &__dialog-title {
    margin: 0 0 12px;
    font-size: 1.125rem;
    font-weight: 600;
    color: #14142b;
  }

  &__dialog-text {
    margin: 0 0 16px;
    font-size: 0.9375rem;
    line-height: 1.55;
    color: #616e7c;
  }

  &__dialog-actions {
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    margin-top: 22px;
    padding-top: 20px;
    border-top: 1px solid #ebf0f4;
  }

  &__btn-cancel,
  &__btn-delete {
    padding: 10px 20px;
    border-radius: 4px;
    font-size: 14px;
    cursor: pointer;
    border: none;
  }

  &__btn-cancel {
    background: #ebf0f4;
    color: #495255;
  }

  &__btn-delete {
    background: #e00b5b;
    color: #fff;
  }
}
</style>
